import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { Link, graphql, useStaticQuery } from "gatsby";
import { Link as ScrollLink, animateScroll as scroll } from "react-scroll";
//assets
import logo from "assets/svg/logo.svg";

//styles
import colors from "styles/colors.js";
import typographySizes from "styles/typographySizes.js";
import distances from "styles/distances.js";
import mediaQuery from "styles/mediaQuery.js";

//components
import MainWrapper from "components/global/MainWrapper.js";

const NavWrapper = styled.div`
  background-color: ${colors.dark};
  width: 100%;
  height: 2rem;
  position: sticky;
margin-top: calc(50vh - 1rem);
top:0;
  left: 0;
pointer-events: auto;
  z-index: 1000;

  a{
    color: ${colors.main};
  }

  display: ${(props) => (props.bottom ? "none" : "block")};

  @media (max-width: ${mediaQuery.tablet}) {
display: block;
    position:  ${(props) => (props.bottom ? "fixed" : "absolute")};
top: ${(props) => (props.bottom ? "initial" : "calc(50dvh - 1rem)")};
bottom: ${(props) => (props.bottom ? "-2rem" : "initial")};
transform: ${(props) =>
  props.scrolled ? "translateY(-2rem)" : "translateY(0rem)"};
    margin-top: 0;
    left: 0;
    width: 100%;
    height: 2rem;
    transition: all 0.3s ease;

&.open{
  transition: all 0.3s ease;
  height: calc(50dvh + 1rem);
  top: ${(props) => (props.bottom ? "initial" : "calc(50dvh - 1rem)")};
    bottom: ${(props) => (props.bottom ? "-2rem" : "initial")};
}

  }
`;

const linkStyles = css`
text-decoration: none;
text-transform: uppercase;
font-size: ${typographySizes.text.tekstSize1.size.desktop}rem;
height: 2rem;
line-height: 2rem;
padding: 0 0.3rem;
position: relative;
z-index: 1;
opacity: ${(props) => (props.$dim ? 0.2 : 1)};

@media (max-width: ${mediaQuery.tablet}) {
  font-size: ${typographySizes.text.tekstSize3.size.mobile}rem;
height: initial;
  line-height: initial;
}
`;

const StyledLink = styled(Link)`
  ${linkStyles}
`;

const StyledExternalLink = styled.a`
  ${linkStyles}
`;

const LinksWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;

  @media (max-width: ${mediaQuery.tablet}) {
    position: absolute;
    flex-direction: column;
    justify-content: center;
    gap: 2rem;
    top: 2rem;
    left: 0;
    background-color: ${colors.dark};
    width: 100%;
    height: 0;
    overflow: hidden;
    transition: all 0.3s ease;
&.open{
  height: calc(100% - 2rem);
  transition: all 0.3s ease;
}
  }
`;

const HoverIndicator = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: ${colors.secondary};
  pointer-events: none;
  transform: translateX(0);
  transition: transform 200ms ease, width 200ms ease, opacity 150ms ease;
  opacity: 0;
  z-index: 0;
  @media (max-width: ${mediaQuery.tablet}) {
    display: none;
  }
`;

const MobileBtn = styled.div`
position: absolute;
top: 0;
left: 50%;
transform: translateX(-50%);
height: 2rem;
flex-direction: column;
pointer-events: auto;
z-index: 2;
cursor: pointer;
transition: all 0.3s ease;
overflow: hidden;
display: none;
@media (max-width: ${mediaQuery.tablet}) {
  display: flex;
}
&.open span{
transform: translateY(-100%);
transition: all 0.3s ease;
}
span{
  transition: all 0.3s ease;
  font-size: ${typographySizes.text.tekstSize2.size.desktop}rem;
  color: ${colors.main};
  text-align: center;
}
`;

const NavLinks = ({ locale, bottom, logoClickTick }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const linksWrapperRef = useRef(null);
  const prevBodyOverflowRef = useRef("");
  const prevHtmlOverflowRef = useRef("");
  const [indicatorStyle, setIndicatorStyle] = useState({
    width: 0,
    left: 0,
    visible: false,
  });
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const data = useStaticQuery(graphql`
    query {
      allStrapiNavbar {
        edges {
          node {
            locale
            Portfolio_external_url
            Services_external_url
            Contact_external_url
          }
        }
      }
    }
  `);

  const navbar =
    data.allStrapiNavbar.edges
      .map(({ node }) => node)
      .find((node) => node.locale === locale) || null;

  const externalUrl = (value) => {
    const trimmed = typeof value === "string" ? value.trim() : "";
    return trimmed || null;
  };

  const links = [
    {
      label: locale === "en" ? "Home" : "Strona główna",
      to: locale === "en" ? "/" : "/pl",
    },
    // {
    //   label: locale === "en" ? "About" : "O nas",
    //   to: locale === "en" ? "/about" : "/pl/o-nas",
    // },
    {
      label: locale === "en" ? "Portfolio" : "Portfolio",
      to: locale === "en" ? "/portfolio" : "/pl/portfolio",
      externalUrl: externalUrl(navbar?.Portfolio_external_url),
    },
    {
      label: locale === "en" ? "Services" : "Usługi",
      to: locale === "en" ? "/services" : "/pl/uslugi",
      externalUrl: externalUrl(navbar?.Services_external_url),
    },
    {
      label: locale === "en" ? "Contact" : "Kontakt",
      to: locale === "en" ? "/contact" : "/pl/kontakt",
      externalUrl: externalUrl(navbar?.Contact_external_url),
    },
  ];

  const updateIndicatorFromElement = (element, show = true) => {
    if (!element || !linksWrapperRef.current) return;
    const wrapperRect = linksWrapperRef.current.getBoundingClientRect();
    const rect = element.getBoundingClientRect();
    setIndicatorStyle({
      width: rect.width,
      left: rect.left - wrapperRect.left,
      visible: show,
    });
  };

  const setActiveIndicator = () => {
    const activeEl = linksWrapperRef.current?.querySelector(".active") || null;
    if (activeEl) {
      updateIndicatorFromElement(activeEl, true);
    } else {
      setIndicatorStyle((prev) => ({ ...prev, visible: false }));
    }
  };

  useEffect(() => {
    setActiveIndicator();
    // Recalculate on resize to keep alignment correct
    const onResize = () => setActiveIndicator();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // We only need to rerun when locale changes (links change)
  }, [locale]);

  useEffect(() => {
    const firstLink = linksWrapperRef.current?.querySelector("a") || null;
    if (firstLink) {
      updateIndicatorFromElement(firstLink, true);
    } else {
      setActiveIndicator();
    }
  }, [logoClickTick]);

  const handleHover = (element) => {
    updateIndicatorFromElement(element, true);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
    setActiveIndicator();
  };

  const scrollToFooter = () => {
    if (typeof document === "undefined") return false;
    const footer = document.getElementById("footer");
    if (!footer) return false;
    footer.scrollIntoView({ behavior: "smooth" });
    return true;
  };

  const handleLinkClick = (index, linkExternalUrl) => (event) => {
    const isLastLink = index === links.length - 1;
    if (isLastLink && !linkExternalUrl) {
      const didScroll = scrollToFooter();
      if (didScroll) {
        event.preventDefault();
      }
    }
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > window.innerHeight * 0.51);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const body = typeof document !== "undefined" ? document.body : null;
    const html =
      typeof document !== "undefined" ? document.documentElement : null;
    const tabletBreakpoint =
      typeof window !== "undefined"
        ? parseInt(mediaQuery.tablet, 10)
        : Number.NaN;
    if (!body || !html || Number.isNaN(tabletBreakpoint)) return undefined;

    const restoreOverflow = () => {
      body.style.overflow = prevBodyOverflowRef.current || "";
      html.style.overflow = prevHtmlOverflowRef.current || "";
    };

    const syncOverflow = () => {
      const isMobile = window.innerWidth <= tabletBreakpoint;
      if (open && isMobile) {
        if (body.style.overflow !== "hidden") {
          prevBodyOverflowRef.current = body.style.overflow;
        }
        if (html.style.overflow !== "hidden") {
          prevHtmlOverflowRef.current = html.style.overflow;
        }
        body.style.overflow = "hidden";
        html.style.overflow = "hidden";
      } else {
        restoreOverflow();
      }
    };

    syncOverflow();
    window.addEventListener("resize", syncOverflow);

    return () => {
      window.removeEventListener("resize", syncOverflow);
      restoreOverflow();
    };
  }, [open]);

  return (
    <>
      <NavWrapper
        bottom={bottom}
        className={open ? "open" : ""}
        scrolled={scrolled}
      >
        <MobileBtn
          className={open ? "open" : ""}
          onClick={() => setOpen(!open)}
        >
          <span>Menu</span>
          <span>close</span>
        </MobileBtn>
        <MainWrapper>
          <LinksWrapper
            ref={linksWrapperRef}
            onMouseLeave={handleMouseLeave}
            className={open ? "open" : ""}
          >
            <HoverIndicator
              style={{
                width: `${indicatorStyle.width}px`,
                transform: `translateX(${indicatorStyle.left}px)`,
                opacity: indicatorStyle.visible ? 1 : 0,
              }}
            />
            {links.map((link, index) => {
              const sharedProps = {
                $dim: hoveredIndex !== null && hoveredIndex !== index,
                onClick: handleLinkClick(index, link.externalUrl),
                onMouseEnter: (e) => {
                  setHoveredIndex(index);
                  handleHover(e.currentTarget);
                },
              };

              if (link.externalUrl) {
                return (
                  <StyledExternalLink
                    key={link.label}
                    href={link.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...sharedProps}
                  >
                    {link.label}
                  </StyledExternalLink>
                );
              }

              return (
                <StyledLink
                  key={link.to}
                  to={link.to}
                  activeClassName="active"
                  {...sharedProps}
                >
                  {link.label}
                </StyledLink>
              );
            })}
          </LinksWrapper>
        </MainWrapper>
      </NavWrapper>
    </>
  );
};

export default NavLinks;
